import React, { useEffect, useState } from "react";
import { addDoc, collection, db } from "../firebaseConfig";
import { message, Modal, Select, Tooltip } from "antd";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./orders.css";
import { generateInvoiceNumber } from "../util/generateInvoiceNumber";
import { getDocs } from "firebase/firestore";
import { generateInvoicePdf } from "../util/generateInvoicePdf";
import CustomTitleSelect from "./CustomTitleComponent";
import CustomSelect from "./CustomSelect";
import { FaInfoCircle } from "react-icons/fa";

import CustomCountrySelect from "./CustomCountrySelect";

const REFERRAL_OPTIONS = [
  "Social Media (LinkedIn, Facebook, Twitter)",
  "Google/Search",
  "Colleague/Word of Mouth",
  "Association (e.g., UAEFMA)",
  "Email Newsletter",
  "Other",
] as const;

type Participant = {
  title: string;
  fname: string;
  lname: string;
  email: string;
  confirmEmail: string;
  organization: string;
  position: string;
  mobileNumber: string;
  ticketId: string;
  nationality: string;
  country: string;

  referralSource?: ReferralOption | "";
  referralOther?: string;
};

export function toBase64(uint8: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < uint8.length; i++) {
    binary += String.fromCharCode(uint8[i]);
  }
  return btoa(binary);
}

interface OrdersV4ModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketId: number;
  setSnackbar: (snackbar: {
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }) => void;
}

type ReferralOption = (typeof REFERRAL_OPTIONS)[number];

// which options require a free-text "specify"
const REFERRAL_REQUIRES_TEXT = new Set<ReferralOption>(["Other"]);

const ticketKeyMap: Record<number, "delegate" | "uaefma" | "exhibitor"> = {
  1: "delegate",
  2: "uaefma",
  3: "exhibitor", // new
};

const TICKET_DETAILS: Record<
  "delegate" | "uaefma" | "exhibitor",
  { label: string; price: number }
> = {
  delegate: { label: "Delegate", price: 1950 },
  uaefma: { label: "UAEFMA Member", price: 950 },
  exhibitor: { label: "Exhibitor", price: 0 }, // new
};

const OrdersV4Modal: React.FC<OrdersV4ModalProps> = ({
  isOpen,
  onClose,
  ticketId,
  setSnackbar,
}) => {
  const stringTicketId = ticketKeyMap[ticketId];

  const [participant, setParticipant] = useState<Participant>({
    title: "",
    fname: "",
    lname: "",
    email: "",
    confirmEmail: "",
    organization: "",
    position: "",
    mobileNumber: "",
    ticketId: stringTicketId,
    nationality: "",
    country: "",
    referralSource: "",
    referralOther: "",
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof Participant, boolean>>
  >({});
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [couponAlert, setCouponAlert] = useState("");
  const [coupons, setCoupons] = useState<any[]>([]);

  const [isExhibitor, setIsExhibitor] = useState(false);

  const validTicketIds = ["delegate", "uaefma", "exhibitor"] as const;
  type TicketKey = (typeof validTicketIds)[number];

  const effectiveTicketId: TicketKey =
    isExhibitor && coupon.includes("Exhibitor") ? "exhibitor" : stringTicketId;
  // const effectiveTicketId: TicketKey = stringTicketId;

  const isValidTicket = (id: string): id is TicketKey =>
    validTicketIds.includes(id as TicketKey);

  // Exhibitor ticket only valid with correct code
  // const isExhibitorValid =
  //   participant.ticketId === "exhibitor" && coupon === "Exhibitor";
  const isExhibitorValid = isExhibitor && coupon.includes("Exhibitor");

  // const basePrice = isValidTicket(effectiveTicketId)
  //   ? isExhibitorValid
  //     ? 0
  //     : TICKET_DETAILS[effectiveTicketId].price
  //   : 0;
  const basePrice = TICKET_DETAILS[stringTicketId].price;

  const discountedTotal = couponAlert.includes("off")
    ? (
        basePrice -
        (couponAlert.includes("%") ? (basePrice * discount) / 100 : discount)
      ).toFixed(2)
    : basePrice.toFixed(2);

  const [hasCompanion, setHasCompanion] = useState(false);
  const [companion, setCompanion] = useState({
    fullName: "",
    relationship: "",
  });

  useEffect(() => {
    const fetchCoupons = async () => {
      const snapshot = await getDocs(collection(db, "coupons"));
      const list = snapshot.docs.map((doc: any) => doc.data());
      setCoupons(list);
    };
    fetchCoupons();
  }, []);

  useEffect(() => {
    setParticipant((prev) => ({ ...prev, ticketId: effectiveTicketId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  // const applyCoupon = () => {
  //   const found = coupons.find((c) => c.code === coupon && c.active);
  //   if (found) {
  //     setDiscount(found.discount);
  //     setCouponAlert(
  //       found.discountType === "percentage"
  //         ? `Coupon applied: ${found.discount}% off!`
  //         : `Coupon applied: $${found.discount} off!`
  //     );
  //   } else {
  //     setDiscount(0);
  //     setCouponAlert("Invalid coupon code");
  //   }
  // };
  const applyCoupon = () => {
    const trimmedCode = coupon.trim();
    const includesExhibitor = trimmedCode.includes("Exhibitor");

    // Case 1: Code includes "Exhibitor" but checkbox is not ticked
    if (includesExhibitor && !isExhibitor) {
      setDiscount(0);
      setCouponAlert("Invalid coupon code");
      return;
    }

    // Case 2: Checkbox is ticked but code does not include "Exhibitor"
    if (isExhibitor && !includesExhibitor) {
      setDiscount(0);
      setCouponAlert("Invalid coupon code");
      return;
    }

    const found = coupons.find((c) => c.code === trimmedCode && c.active);

    if (found) {
      setDiscount(found.discount);
      setCouponAlert(
        found.discountType === "percentage"
          ? `Coupon applied: ${found.discount}% off!`
          : `Coupon applied: $${found.discount} off!`
      );
    } else {
      setDiscount(0);
      setCouponAlert("Invalid coupon code");
    }
  };

  const [loading, setLoading] = useState(false);

  const [linkedinEnabled, setLinkedinEnabled] = useState(false);
  const [isDev, setIsDev] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      const snapshot = await getDocs(collection(db, "settings"));
      const data = snapshot.docs[0]?.data();
      if (data) {
        setLinkedinEnabled(!!data.enableLinkedInPost);
        setIsDev(!!data.isDev);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const fetchLinkedInSetting = async () => {
      const snapshot = await getDocs(collection(db, "settings"));
      const data = snapshot.docs[0]?.data();
      if (data?.enableLinkedInPost) {
        setLinkedinEnabled(true);
      }
    };
    fetchLinkedInSetting();
  }, []);

  const confirmLinkedInPost = (): Promise<boolean> =>
    new Promise((resolve) => {
      Modal.confirm({
        title: "Post to LinkedIn?",
        content: "Do you want to share your registration on LinkedIn?",
        okText: "Yes, post it",
        cancelText: "No",
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

  const postToLinkedInHandler = async (participant: Participant) => {
    // Optional: store name for personalized post
    sessionStorage.setItem(
      "postName",
      `${participant.fname} ${participant.lname}`
    );

    const clientId = "77kay9ne9r4a45";
    const redirectUri = encodeURIComponent(
      "https://icaconferencedubai.com/linkedin"
    );
    const scope = encodeURIComponent("w_member_social r_liteprofile");
    const state = "ica-conference";

    const linkedInUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}`;

    window.location.href = linkedInUrl;
  };

  const handleOrder = async () => {
    setLoading(true);

    const newErrors: typeof errors = {};
    if (!participant.fname) newErrors.fname = true;
    if (!participant.lname) newErrors.lname = true;
    if (!participant.email) newErrors.email = true;
    if (!participant.confirmEmail) newErrors.confirmEmail = true;
    if (!participant.mobileNumber) newErrors.mobileNumber = true;
    if (participant.email !== participant.confirmEmail) {
      newErrors.email = true;
      newErrors.confirmEmail = true;
    }
    if (!participant.organization) newErrors.organization = true;
    if (!participant.position) newErrors.position = true;

    if (isExhibitor && !coupon.includes("Exhibitor")) {
      message.error("Coupon code is required for Exhibitor registration.");
      setLoading(false);
      return;
    }

    if (!isExhibitor && coupon.includes("Exhibitor")) {
      message.error("Invalid coupon code.");
      setLoading(false);
      return;
    }
    // make referral required if you want; otherwise leave source optional
    if (!participant.referralSource) newErrors.referralSource = true;

    // if selected option needs details, enforce "specify"
    if (
      participant.referralSource &&
      REFERRAL_REQUIRES_TEXT.has(participant.referralSource) &&
      !participant.referralOther?.trim()
    ) {
      newErrors.referralOther = true;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      message.error("Please complete all required fields correctly.");
      setLoading(false);
      return;
    }

    // 🔍 Check for duplicate email
    const ordersSnapshot = await getDocs(collection(db, "orders"));
    for (const orderDoc of ordersSnapshot.docs) {
      const contactsRef = collection(db, `orders/${orderDoc.id}/contacts`);
      const contactsSnapshot = await getDocs(contactsRef);
      for (const contactDoc of contactsSnapshot.docs) {
        const data = contactDoc.data();
        if (
          data.email &&
          data.email.toLowerCase() === participant.email.toLowerCase()
        ) {
          message.error("This email has already been registered.");
          setLoading(false);
          return;
        }
      }
    }

    // ✅ DEV MODE — skip to LinkedIn logic
    if (isDev && linkedinEnabled) {
      try {
        // const confirmed = await confirmLinkedInPost();
        // if (confirmed) {
        //   await postToLinkedInHandler(participant);
        // }
        const confirmed = await confirmLinkedInPost();
        if (confirmed) {
          await postToLinkedInHandler(participant); // triggers LinkedIn OAuth flow
        }
        setSnackbar({
          open: true,
          message: "[DEV] Skipped registration. LinkedIn flow executed.",
          severity: "info",
        });
      } catch (err: any) {
        setSnackbar({
          open: true,
          message: err.message || "Error in LinkedIn flow",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const invoiceNumber = await generateInvoiceNumber();
      const totalDelegateTickets = stringTicketId === "delegate" ? 1 : 0;
      const totalUaefmaTickets = stringTicketId === "uaefma" ? 1 : 0;

      const orderRef = await addDoc(collection(db, "orders"), {
        totalDelegateTickets,
        totalUaefmaTickets,
        coupon,
        totalAmount: discountedTotal,
        ticketId: effectiveTicketId,
        invoiceNumber, // 🔐 Assign invoice number here
        timestamp: new Date(),
      });

      await addDoc(collection(db, `orders/${orderRef.id}/contacts`), {
        ...participant,
        companion: hasCompanion ? companion : null,
        ticketId: effectiveTicketId,
      });

      const pdfBytes = await generateInvoicePdf(
        {
          fullName: `${participant.fname || ""} ${
            participant.lname || ""
          }`.trim(),
          email: participant.email || "",
          mobile: participant.mobileNumber || "",
          company: participant.organization || "",
          position: participant.position || "",
        },
        invoiceNumber,
        discountedTotal,
        TICKET_DETAILS[effectiveTicketId].label,
        1,
        basePrice,
        basePrice - parseFloat(discountedTotal)
      );

      // Sending Email
      const functionUrl = isDev
        ? "https://us-central1-icaconference-438ba.cloudfunctions.net/sendEmailDev"
        : "https://us-central1-icaconference-438ba.cloudfunctions.net/sendEmailV2";

      await fetch(functionUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          personalizations: [
            {
              to: [{ email: participant.email }],
              dynamic_template_data: {
                title: participant.title || "",
                fullName: `${participant.title || ""} ${
                  participant.fname || ""
                } ${participant.lname || ""}`,
                email: participant.email || "",
                mobile: participant.mobileNumber || "",
                company: participant.organization || "",
                position: participant.position || "",
                country: participant.country || "",
                nationality: participant.nationality || "",
                invoiceNumber: invoiceNumber || "N/A",
                invoiceDate: new Date().toLocaleString(),
                companionName: hasCompanion ? companion.fullName : "",
                companionRelationship: hasCompanion
                  ? companion.relationship
                  : "",
              },
            },
          ],
          attachments: [
            {
              content: toBase64(pdfBytes),
              filename: `Invoice-${invoiceNumber}.pdf`,
              type: "application/pdf",
              disposition: "attachment",
            },
          ],
          from: {
            email: "registration@comms-dev.com",
            name: "ICA Conference",
          },
          subject: "Your ICA Conference Invoice",
          template_id: "d-54a4a52ee7934670be836f8614cb4cd2",
        }),
      });

      if (linkedinEnabled) {
        const confirmed = await confirmLinkedInPost();
        if (confirmed) {
          await postToLinkedInHandler(participant);
        }
      }

      setSnackbar({
        open: true,
        message: "Registration successful!",
        severity: "success",
      });

      setParticipant({
        title: "",
        fname: "",
        lname: "",
        email: "",
        confirmEmail: "",
        organization: "",
        position: "",
        mobileNumber: "",
        ticketId: effectiveTicketId,
        nationality: "",
        country: "",
      });

      setCoupon("");
      setCouponAlert("");
      setErrors({});
      onClose();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || "Error completing registration",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isMobile = window.innerWidth <= 768;

  const notMobileStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "flex-start" /* aligns label tops */,
    gap: "24px",
    flexWrap: "wrap",
    marginTop: "12px",
    paddingBottom: 40,
  };
  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header" style={{ paddingBottom: 20 }}>
          <h1 className="title">
            Register – {TICKET_DETAILS[stringTicketId].label}
          </h1>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="modal-body">
          <div className="personal-info-column">
            {/* Title + First Name + Last Name */}
            <div className="info-column title-name-group">
              <CustomTitleSelect
                value={participant.title}
                onChange={(val) =>
                  setParticipant({ ...participant, title: val })
                }
              />

              <input
                type="text"
                placeholder="First Name"
                value={participant.fname}
                onChange={(e) =>
                  setParticipant({ ...participant, fname: e.target.value })
                }
                className={errors.fname ? "input-error" : ""}
              />
              <input
                type="text"
                placeholder="Last Name"
                value={participant.lname}
                onChange={(e) =>
                  setParticipant({ ...participant, lname: e.target.value })
                }
                className={errors.lname ? "input-error" : ""}
              />
            </div>

            {/* Email + Confirm Email */}
            <div className="info-column">
              <input
                type="text"
                placeholder="Email"
                value={participant.email}
                onChange={(e) =>
                  setParticipant({ ...participant, email: e.target.value })
                }
                className={errors.email ? "input-error" : ""}
              />
              <input
                type="text"
                placeholder="Confirm Email"
                value={participant.confirmEmail}
                onChange={(e) =>
                  setParticipant({
                    ...participant,
                    confirmEmail: e.target.value,
                  })
                }
                className={errors.confirmEmail ? "input-error" : ""}
              />
            </div>

            {/* Mobile + Country + Nationality */}
            <div className="info-column">
              {/* <input
                type="text"
                placeholder="Country"
                value={participant.country}
                onChange={(e) =>
                  setParticipant({ ...participant, country: e.target.value })
                }
                className={errors.country ? "input-error" : ""}
              /> */}

              <CustomCountrySelect
                value={participant.country}
                onChange={(val) =>
                  setParticipant({ ...participant, country: val })
                }
                className={errors.country ? "input-error" : ""}
              />

              {/* <input
                type="text"
                placeholder="Nationality"
                value={participant.nationality}
                onChange={(e) =>
                  setParticipant({
                    ...participant,
                    nationality: e.target.value,
                  })
                }
                className={errors.nationality ? "input-error" : ""}
              /> */}

              <CustomCountrySelect
                label="Select Nationality"
                value={participant.nationality}
                onChange={(val) =>
                  setParticipant({ ...participant, nationality: val })
                }
                className={errors.nationality ? "input-error" : ""}
              />
            </div>

            {/* Organization + Position */}
            <div className="info-column" style={{ paddingTop: 12 }}>
              <input
                type="text"
                placeholder="Organization"
                value={participant.organization}
                onChange={(e) =>
                  setParticipant({
                    ...participant,
                    organization: e.target.value,
                  })
                }
                className={errors.organization ? "input-error" : ""}
              />
              <input
                type="text"
                placeholder="Position"
                value={participant.position}
                onChange={(e) =>
                  setParticipant({ ...participant, position: e.target.value })
                }
                className={errors.position ? "input-error" : ""}
              />
            </div>

            {/* Mobile + Country + Nationality */}
            <div
              style={{
                display: "flex",
                gap: 20,
                width: "100%",
                flexWrap: "wrap",
              }}
            >
              {/* Phone Number */}
              <div style={{ flex: 1, minWidth: 200 }}>
                <PhoneInput
                  placeholder="Mobile Number (e.g +971 xx xxx xxxx)"
                  value={participant.mobileNumber || ""}
                  international
                  defaultCountry="AE"
                  onChange={(val) =>
                    setParticipant({ ...participant, mobileNumber: val || "" })
                  }
                  className={`input ${
                    errors.mobileNumber ? "input-error" : ""
                  }`}
                  style={{ width: "100%", maxHeight: 450 }}
                />
              </div>

              {/* Companion Radio + Info */}
              <div style={{ flex: 1, minWidth: 220 }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontWeight: 500,
                    marginBottom: 8,
                    gap: 6,
                  }}
                >
                  Do you have a companion?
                  <span className="companion-note-wrap">
                    <Tooltip
                      placement="top"
                      title="Companions are ONLY allowed at evening activities. They cannot attend business sessions or working lunches."
                    >
                      <span
                        className="companion-note-icon"
                        role="img"
                        aria-label="Companion access info"
                      >
                        <FaInfoCircle />
                      </span>
                    </Tooltip>
                    {/* Inline helper text (hidden on small screens via CSS) */}
                    <span className="companion-note-text">
                      Companions are ONLY allowed at evening activities. They
                      cannot attend business sessions or working lunches.
                    </span>
                  </span>
                </label>

                <div style={{ display: "flex", gap: 16 }}>
                  <label
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <input
                      type="radio"
                      checked={hasCompanion === true}
                      onChange={() => {
                        setHasCompanion(true);
                        setCompanion({ fullName: "", relationship: "" });
                      }}
                    />
                    Yes
                  </label>
                  <label
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <input
                      type="radio"
                      checked={hasCompanion === false}
                      onChange={() => {
                        setHasCompanion(false);
                        setCompanion({ fullName: "", relationship: "" });
                      }}
                    />
                    No
                  </label>
                </div>
              </div>
            </div>

            {hasCompanion && (
              <>
                <label style={{ fontWeight: 500, marginTop: 20 }}>
                  Companion Details
                </label>
                <div className="info-column" style={{ marginTop: 8 }}>
                  <input
                    type="text"
                    placeholder="Companion's Full Name"
                    value={companion.fullName}
                    onChange={(e) =>
                      setCompanion({ ...companion, fullName: e.target.value })
                    }
                    className="input"
                  />
                  {/* <input
                    type="text"
                    placeholder="Relationship (e.g., Spouse, Friend)"
                    value={companion.relationship}
                    onChange={(e) =>
                      setCompanion({
                        ...companion,
                        relationship: e.target.value,
                      })
                    }
                    className="input"
                  /> */}
                  <CustomSelect
                    label="Relationship"
                    options={["Husband", "Wife"]}
                    value={companion.relationship}
                    onChange={(val) =>
                      setCompanion({
                        ...companion,
                        relationship: val,
                      })
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div style={!isMobile ? notMobileStyle : { paddingBottom: 40 }}>
          {/* LEFT: How did you hear about us + Group Registration */}
          <div className="referral-col">
            <label>How did you hear about us?</label>

            <CustomSelect
              label="Select an option"
              options={[...REFERRAL_OPTIONS]}
              value={participant.referralSource || ""}
              className={errors.referralSource ? "input-error" : ""}
              onChange={(val: string) =>
                setParticipant({
                  ...participant,
                  referralSource: val as ReferralOption,
                  referralOther:
                    val === "Other" ? participant.referralOther || "" : "",
                })
              }
            />

            {participant.referralSource === "Other" && (
              <input
                type="text"
                placeholder="Please specify"
                value={participant.referralOther || ""}
                onChange={(e) =>
                  setParticipant({
                    ...participant,
                    referralOther: e.target.value,
                  })
                }
                className={errors.referralOther ? "input-error" : ""}
                style={{
                  flex: 1,
                  padding: "10px",
                  marginTop: 18,
                  borderRadius: 6,
                }}
              />
            )}

            <div style={{ fontSize: "13px", color: "#777", marginTop: 14 }}>
              <strong>Group Registration?</strong>
              <p style={{ marginTop: 6, lineHeight: 1.5 }}>
                Contact us at{" "}
                <a href="mailto:eman@uaefma.com" style={{ color: "#1E88E5" }}>
                  eman@uaefma.com
                </a>
              </p>
            </div>
          </div>

          {/* RIGHT: I’m an Exhibitor + Coupon + Totals + Register */}
          <div className="exhibitor-col">
            <div className="checkbox-row">
              <input
                type="checkbox"
                id="isExhibitor"
                checked={isExhibitor}
                onChange={(e) => {
                  setIsExhibitor(e.target.checked);
                  setCoupon("");
                  setCouponAlert("");
                  setDiscount(0);
                }}
                className="checkbox-input"
              />
              <label
                htmlFor="isExhibitor"
                style={{ color: "#fff", paddingTop: 14 }}
              >
                I’m an Exhibitor
              </label>
            </div>

            <label htmlFor="coupon">Coupon Code</label>
            <div>
              <input
                id="coupon"
                type="text"
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
                placeholder="Enter coupon code"
                className="input"
                style={{ flex: 1, padding: "10px", borderRadius: 6 }}
              />
              <button
                className="btn-register"
                onClick={applyCoupon}
                style={{
                  fontSize: 16,
                  padding: "0 16px",
                  height: 44,
                  marginTop: 4,
                }}
              >
                Apply
              </button>
            </div>

            {couponAlert && (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 14,
                  color: couponAlert.includes("Invalid")
                    ? "#D32F2F"
                    : "#e7a73b",
                }}
              >
                {couponAlert}
              </div>
            )}

            <div style={{ fontSize: 14, color: "#888", marginTop: 8 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "4px 0",
                }}
              >
                <span>Ticket Price:</span>
                <span>${basePrice.toFixed(2)}</span>
              </div>
              {couponAlert.includes("off") && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "4px 0",
                  }}
                >
                  <span>Discount:</span>
                  <span>
                    -$
                    {couponAlert.includes("%")
                      ? ((basePrice * discount) / 100).toFixed(2)
                      : discount.toFixed(2)}
                  </span>
                </div>
              )}
              <hr
                style={{
                  border: "none",
                  borderTop: "1px dashed #ccc",
                  margin: "8px 0",
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: 600,
                  fontSize: 16,
                  color: "#e7a73b",
                }}
              >
                <span>Total:</span>
                <span>${discountedTotal}</span>
              </div>
            </div>

            <button
              className="btn-register"
              disabled={couponAlert.includes("Invalid") || loading}
              onClick={handleOrder}
              style={{
                marginTop: "1.75rem",
                width: "100%",
                padding: 12,
                fontSize: 16,
              }}
            >
              {loading ? (
                <span
                  className="spinner"
                  style={{
                    display: "inline-block",
                    width: 20,
                    height: 20,
                    border: "2px solid #ccc",
                    borderTop: "2px solid #e7a73b",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
              ) : (
                "Register"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrdersV4Modal;
